// eslint-disable-next-line jira/filename-case
import { useCallback } from 'react';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useLicensedProducts } from '@atlassian/jira-tenant-context-controller/src/components/licensed-products/index.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { hasProductAccess } from '../permission-check/index.tsx';
import { useFetchIsRequestAccessCapable } from '../request-capabilities/index.tsx';
import { isLoomSite7DaysOld } from '../site-eligibility/index.tsx';
import { isSiteOptedOut } from '../site-opt-out/index.tsx';
import { isNewActiveUser } from '../user-eligibility/index.tsx';
import {
	getFeatureVisited,
	getHasSeenNudgeInLast7Days,
	withEligibilityCache,
} from '../caching/index.tsx';
import { isExcludedForNav4Placement } from '../cjn-nav4-placement/isExcludedForNav4Placement.tsx';

export type LoomCJNIneligibilityReason =
	| 'NEW_ACTIVE_USER'
	| 'NEW_SITE_LESS_THAN_7_DAYS_OLD'
	| 'ALREADY_HAVE_ACCESS'
	| 'NOT_ELIGIBLE_FOR_REQUEST_ACCESS'
	| 'NO_LOOM_LICENCE'
	| 'SITE_OPTED_OUT'
	| 'RECENTLY_SEEN_CROSS_JOIN_NUDGE'
	| 'STILL_ON_SNOOZE'
	| 'EXCLUDED_FOR_NAV4_PLACEMENT_EXPERIMENT';

export type LoomCJNEligibilityCheckResult =
	| {
			eligible: false;
			ineligibilityReason: LoomCJNIneligibilityReason;
	  }
	| { eligible: true };

export const useCheckLoomCrossJoinNudgeEligibility =
	(): (() => Promise<LoomCJNEligibilityCheckResult>) => {
		const cloudId = useCloudId();
		const userId = useAccountId() || '';
		const licensedProducts = useLicensedProducts();
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const fetchIsRequestAccessCapable = useFetchIsRequestAccessCapable('crossJoinNudgesJiraToLoom');
		return useCallback(async (): Promise<LoomCJNEligibilityCheckResult> => {
			const cachedIsSiteOptedOut = withEligibilityCache('site_is_opted_out', isSiteOptedOut, 0.2);
			const cachedIsSite7DaysOld = withEligibilityCache(
				'loom_site_is_more_than_7_days_old',
				isLoomSite7DaysOld,
				1,
			);
			const cachedIsNewActiveUser = withEligibilityCache(
				'user_is_too_new_for_loom',
				isNewActiveUser,
				1,
			);
			const cachedHasProductAccess = withEligibilityCache(
				'user_already_has_loom_access',
				hasProductAccess,
				0.2,
			);
			const cachedIsRequestAccessCapable = withEligibilityCache(
				'user_can_request_loom_access',
				fetchIsRequestAccessCapable,
				0.2,
			);

			// if the snooze is still active, return false
			if (await getHasSeenNudgeInLast7Days()) {
				return {
					eligible: false,
					ineligibilityReason: 'RECENTLY_SEEN_CROSS_JOIN_NUDGE',
				};
			}

			if (getFeatureVisited('crossJoinNudgesJiraToLoom')) {
				return {
					eligible: false,
					ineligibilityReason: 'STILL_ON_SNOOZE',
				};
			}

			if (!licensedProducts.loom) {
				return {
					eligible: false,
					ineligibilityReason: 'NO_LOOM_LICENCE',
				};
			}

			if (await cachedIsSiteOptedOut(cloudId)) {
				return {
					eligible: false,
					ineligibilityReason: 'SITE_OPTED_OUT',
				};
			}

			if (!(await cachedIsSite7DaysOld(cloudId))) {
				return {
					eligible: false,
					ineligibilityReason: 'NEW_SITE_LESS_THAN_7_DAYS_OLD',
				};
			}

			if (await cachedIsNewActiveUser(cloudId, userId, 14)) {
				return {
					eligible: false,
					ineligibilityReason: 'NEW_ACTIVE_USER',
				};
			}

			// has access to loom on this site
			if (await cachedHasProductAccess('loom', cloudId)) {
				return {
					eligible: false,
					ineligibilityReason: 'ALREADY_HAVE_ACCESS',
				};
			}

			if (!(await cachedIsRequestAccessCapable('loom', cloudId, createAnalyticsEvent))) {
				return {
					eligible: false,
					ineligibilityReason: 'NOT_ELIGIBLE_FOR_REQUEST_ACCESS',
				};
			}

			if (await isExcludedForNav4Placement(cloudId, userId, createAnalyticsEvent, 'loom')) {
				return { eligible: false, ineligibilityReason: 'EXCLUDED_FOR_NAV4_PLACEMENT_EXPERIMENT' };
			}

			return {
				eligible: true,
			};
		}, [cloudId, createAnalyticsEvent, fetchIsRequestAccessCapable, licensedProducts.loom, userId]);
	};
